<template>
  <div
    class="bg-black-card mt-2.5 rounded-lg p-2.5 overflow-hidden flex flex-col"
    :style="{
      minHeight: 40 * (1 + 3) + 20 + 'px',
      maxHeight: 40 * (1 + 4) + 20 + 'px'
    }"
  >
    <!-- 样式title -->
    <div class="px-5 flex items-center justify-between h-10 flex-shrink-0">
      <span class="text-sm">样式列表</span>
      <a-tooltip v-if="showAddThemeBtn" title="新增样式">
        <icon
          class="cursor-pointer"
          :class="{
            'opacity-30': themeTree.length >= GlobalConfig.themeCountMax
          }"
          :icon="'add.svg'"
          :width="14"
          @click.stop="addTheme()"
        />
      </a-tooltip>
    </div>
    <!-- 样式数据 -->
    <div class="overflow-overlay h-full flex-1">
      <div v-for="data in themeTree" :key="data.key">
        <drop-down
          :menus="getRightMenus(data as ThemeTreeNode)"
          trigger="contextmenu"
        >
          <div
            class="px-5 flex items-center justify-between border border-transparent"
            :class="{
              ' !border-[#0D84F2]':[hoverd, ].includes((data as ThemeTreeNode).key) ,
              '_invalid': invalidTreeIds.includes((data as ThemeTreeNode).key),
            }"
            tabindex="-1"
            data-theme-node="true"
            @mousemove.stop="() => (hoverd = (data as ThemeTreeNode).key)"
            @mouseleave.stop="() => (hoverd = '')"
            @click.stop="()=>onClick(data as ThemeTreeNode)"
          >
            <div class="w-6">
              <icon
                v-if="(data as ThemeTreeNode).extra.themeStyle.id == editorStore.$state.themeId"
                icon="selected.svg"
                :width="15"
                :height="15"
                fill="none"
              />
            </div>
            <EditableTitle
              class="flex-1 h-10 w-full"
              :tree-id="(data as ThemeTreeNode).key"
              :title="(isAOD &&layerStore.isSingleAOD)?'息屏' : (data as ThemeTreeNode).extra.themeStyle.name + (isAOD?'的息屏':'') "
              :submit="rename"
              :valid="validName"
              :edit-key="editKeyMap[(data as ThemeTreeNode).key]"
              :disabled="isAOD"
              @valid="onValid"
            />
          </div>
        </drop-down>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useGlobalShortcuts, useThemeManage } from '@/hooks';
import { useLayerStore } from '@/store/use-layer-store';
import { useEditorStore } from '@/store/use-editor-store';
import { computed, reactive, ref } from 'vue';
import { Modal } from 'ant-design-vue';
import { GlobalConfig } from '@/services/gloabl.config';
import { canUse } from '@/utils/can-use.utils';
const editorStore = useEditorStore();
export interface ThemeTreeNode {
  key: string;
  extra: {
    themeStyle: StoreThemeItem;
  };
  children: false;
}
const hoverd = ref('');
const { addNormalTheme, rename, validName, deleteNormalTheme } =
  useThemeManage();
const layerStore = useLayerStore();
const isAOD = computed(() => editorStore.theme?.type == 'AOD');

const showAddThemeBtn = computed(() => {
  return editorStore.theme?.type == 'normal' && canUse('multi-themes').valid;
});

// 验证重命名
const invalidTreeIds = ref<string[]>([]);
const onValid = (treeId: string, valid: boolean) => {
  const index = invalidTreeIds.value.findIndex((item) => item == treeId);
  if (valid) {
    // delete
    if (index > -1) invalidTreeIds.value.splice(index, 1);
  } else {
    // add
    if (index < 0) invalidTreeIds.value.push(treeId);
  }
};
const editKeyMap = reactive<Record<string, number>>({});
const themeTree = computed(() => {
  const res: ThemeTreeNode[] = [];
  const type = editorStore.theme?.type ?? 'normal';
  const themes = layerStore.displayThemesForType(type);
  themes.forEach((item) => {
    res.push({
      key: item.id,
      extra: { themeStyle: item },
      children: false
    });
  });
  return res;
});

const getRightMenus = (data: ThemeTreeNode) => {
  const themeManage = useThemeManage();
  const themeStyle = data.extra.themeStyle;
  let res: DropDownMenuItem[];
  if (themeStyle.type == 'AOD')
    res = [
      {
        title: '复用亮屏样式图层',
        onClick() {
          themeManage.reuseNormalTheme(themeStyle.id);
          editorStore.selectTheme(themeStyle.id);
        }
      }
    ];
  else
    res = [
      {
        title: '复制',
        disabled: !canUse('multi-themes').valid,
        shortcuts: ['CommandOrControl', 'C'],
        onClick() {
          addTheme(themeStyle);
        }
      },
      {
        title: '重命名',
        shortcuts: ['CommandOrControl', 'R'],
        onClick() {
          editKeyMap[themeStyle.id] = Math.random();
        }
      },
      {
        title: '删除',
        disabled: themeTree.value.length <= 1,
        onClick() {
          deleteNormalTheme(themeStyle.id);
        }
      }
    ];
  return res;
};

useGlobalShortcuts(
  [
    {
      keyboardKeys: ['CommandOrControl', 'R'],
      handler: () => {
        if (editorStore.theme) editKeyMap[editorStore.theme.id] = Math.random();
      }
    },
    {
      keyboardKeys: ['CommandOrControl', 'C'],
      handler: () => {
        if (!canUse('multi-themes').valid) return;
        if (editorStore.theme) addTheme(editorStore.theme);
      }
    }
  ],
  (e: KeyboardEvent) => {
    const targert = e.target as HTMLDivElement;
    return editorStore.theme?.type == 'normal' && !!targert.dataset?.themeNode;
  }
);

// 点击node时
const onClick = (node: ThemeTreeNode) => {
  const themeid = node.extra.themeStyle.id;
  if (editorStore.themeId !== themeid)
    editorStore.selectTheme(node.extra.themeStyle.id);
};

// 新增样式
const addTheme = (theme?: StoreThemeItem) => {
  if (themeTree.value.length >= GlobalConfig.themeCountMax) {
    Modal.warning({
      title: '无法添加',
      content: '样式数量不可超过' + GlobalConfig.themeCountMax + '个',
      okText: '知道了'
    });
  } else {
    if (theme) return addNormalTheme(theme);
    addNormalTheme(editorStore.theme);
  }
};
</script>

<style scoped lang="less">
:deep(.tree) {
  .ant-tree-switcher {
    display: none;
  }
  .ant-tree-treenode {
    padding: 0;
    background: #2e2e2e;
    &:has(._invalid) {
      margin-bottom: 18px;
    }
    .ant-tree-node-content-wrapper {
      cursor: default !important;
    }
  }
  .ant-tree-node-content-wrapper {
    padding: 0;
  }
}
</style>
@/services/gloabl.config
